/*
 * Sección Dashboard: estadísticas del periodo para gerencia, gestión humana y nómina (spec 4.2 a 4.7).
 * Pide GET /attendance/dashboard/ (etapa 3) con TODOS los filtros (zona, tienda y empleado van al servidor)
 * y arma: resumen ejecutivo, 7 KPI con comparación y minigráfica, rankings, hora de llegada (tramos y mapa de calor), tendencia semanal, horas por persona, tiendas y zonas, barras de jornada del día (con las jornadas del
 * contenedor: rows/status/message/onRetry) y alertas de calidad.
 * El servidor admite rangos de hasta 93 días (más estricto que el contenedor, que admite 366): con un rango
 * mayor NO se llama al servidor, se avisa y se ofrece acortar a los últimos 93 días. Mientras carga no se
 * muestran los datos del periodo anterior como actuales (StateView solo pinta el contenido con status 'ok').
 * Recibe además `onFiltersChange` (setFilters del contenedor) para el botón de acortar el rango.
 */
import React from "react";
import {fetchDashboard} from "../../../../redux/attendance";
import useRemoteData from "../hooks/useRemoteData";
import StateView from "../components/StateView";
import SectionHeader from "../components/SectionHeader";
import KpiCard from "../components/KpiCard";
import {addDays, buildParams, daysBetween} from "../utils/period";
import SummaryPanel from "./dashboard/SummaryPanel";
import RankingsPanel from "./dashboard/RankingsPanel";
import ArrivalPanels from "./dashboard/ArrivalPanels";
import TrendPanels from "./dashboard/TrendPanels";
import GroupsPanel from "./dashboard/GroupsPanel";
import DayBarsPanel from "./dashboard/DayBarsPanel";
import AlertsPanel from "./dashboard/AlertsPanel";

export const DASHBOARD_MAX_DAYS = 93;

const Dashboard = ({rows, status, message, onRetry, filters, range, today, token, canExport, onFiltersChange}) => {
    const days = daysBetween(range.desde, range.hasta);
    const tooLong = days > DASHBOARD_MAX_DAYS;
    const params = buildParams(filters, range);
    const dashboard = useRemoteData(
        () => fetchDashboard(token, params),
        token && !tooLong ? JSON.stringify(params) : null,
    );
    const datos = dashboard.data ? dashboard.data.datos : null;
    const isEmpty = dashboard.status === 'ok' && dashboard.data.cantidad === 0;
    const inProgress = range.desde <= today && range.hasta >= today;
    const shorten = () => onFiltersChange({
        ...filters, periodo: 'personalizado', desde: addDays(range.hasta, -(DASHBOARD_MAX_DAYS - 1)), hasta: range.hasta,
    });

    return (
        <section className="mc-section" aria-labelledby="mc-title-dashboard">
            <SectionHeader id="mc-title-dashboard" title="Dashboard" canExport={canExport} token={token} range={range}
                           exportParams={{...params, seccion: 'dashboard'}}/>
            {tooLong ? (
                <div className="mc-state mc-state--error" role="alert">
                    <i className="bx bx-calendar-exclamation" aria-hidden="true"/>
                    <p className="mc-state__title">El dashboard admite rangos de hasta {DASHBOARD_MAX_DAYS} días</p>
                    <p className="mc-state__text">El rango elegido tiene {days} días. Acórtalo para ver las estadísticas.</p>
                    {onFiltersChange &&
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={shorten}>
                            Ver solo los últimos {DASHBOARD_MAX_DAYS} días del rango
                        </button>}
                </div>
            ) : (
                <StateView status={dashboard.status} message={dashboard.message} onRetry={dashboard.reload} isEmpty={isEmpty}>
                    {datos && (
                        <>
                            <SummaryPanel datos={datos} inProgress={inProgress}/>
                            <div className="mc-kpis">
                                {datos.kpis.map((card) => <KpiCard key={card.clave} card={card}/>)}
                            </div>
                            <RankingsPanel datos={datos}/>
                            <ArrivalPanels datos={datos}/>
                            <TrendPanels datos={datos}/>
                            <GroupsPanel datos={datos}/>
                            <DayBarsPanel rows={rows} status={status} message={message} onRetry={onRetry}
                                          filters={filters} range={range} today={today}/>
                            <AlertsPanel alertas={datos.alertas} range={range}/>
                        </>
                    )}
                </StateView>
            )}
        </section>
    );
};

export default Dashboard;
